import numbro from 'numbro'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Skeleton, Typography } from '@blade/ui-kit'
import { Coins2Icon, PriorityIcon } from '@root/shared/ui-kit'

import { GasSettingsNetworkCongestion } from './gas-settings-network-congestion'

import type { SuggestedGasSettings } from '@root/entities/gas-information'
import type { Error } from '@root/shared'

type GasSettingsNetworkStatusProps = {
  gasInformationState: AsyncStateN.AsyncStateN<
    Error.UnknownError,
    SuggestedGasSettings.Info
  >
}

export const GasSettingsNetworkStatus: React.FC<
  GasSettingsNetworkStatusProps
> = ({ gasInformationState }) => {
  const { t } = useTranslation()

  const renderBaseFee = () => {
    if (!AsyncStateN.isSuccess(gasInformationState)) {
      return <Skeleton width={60} height={20} />
    }

    return (
      <MetricCardValue>
        {formatGwei(gasInformationState.value.estimatedBaseFeeGwei)}
        <span> GWEI</span>
      </MetricCardValue>
    )
  }

  const renderPriorityFee = () => {
    if (!AsyncStateN.isSuccess(gasInformationState)) {
      return <Skeleton width={70} height={20} />
    }

    const start = formatGwei(
      gasInformationState.value.latestPriorityFeeRange[0],
    )
    const end = formatGwei(gasInformationState.value.latestPriorityFeeRange[1])

    return (
      <MetricCardValue>
        {start} - {end} GWEI
      </MetricCardValue>
    )
  }

  return (
    <Root>
      <Title>{t('gas-settings.network-status')}</Title>
      <Metrics>
        <MetricCard>
          <MetricIcon>
            <Coins2Icon size={18} />
          </MetricIcon>
          <MetricCardLabel>{t('gas-settings.base-fee')}</MetricCardLabel>
          {renderBaseFee()}
        </MetricCard>
        <MetricCard>
          <MetricIcon>
            <PriorityIcon size={18} />
          </MetricIcon>
          <MetricCardLabel>{t('gas-settings.priority-fee')}</MetricCardLabel>
          {renderPriorityFee()}
        </MetricCard>
      </Metrics>

      <GasSettingsNetworkCongestion
        congestion={gasInformationState.value?.networkCongestion}
      />
    </Root>
  )
}

const Root = styled.div`
  padding-inline: 12px;
`

const Title = styled.div`
  color: var(--text-12);
  text-align: center;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;

  margin-bottom: 16px;
`

const MetricCard = styled.div`
  border-radius: 12px;
  background: var(--backgroundA-2);
  padding: 16px;

  display: grid;
  grid-auto-flow: row;
`

const MetricCardLabel = styled(Typography)`
  color: var(--text-9);
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
  text-transform: uppercase;

  margin-bottom: 2px;
`

const MetricCardValue = styled.div`
  color: var(--text-12);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`

const Metrics = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
  margin-bottom: 16px;
`

const MetricIcon = styled.div`
  width: 36px;
  height: 36px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 8px;
  background: var(--backgroundA-2);
  margin-bottom: 12px;
`

const formatGwei = (num: number) =>
  numbro(num).format({
    trimMantissa: true,
    mantissa: 1,
    thousandSeparated: true,
  })
